import { Alert, Button, Flex, Modal, Typography } from "antd"
import React, { useEffect, useId, useState } from "react"
import { useTranslation } from "react-i18next"

import { TextArea } from "../../Input/TextArea.tsx"
import { refusalText } from "./refusal-text.ts"
import { TOUCH } from "./touch.ts"
import type { Outcome } from "./types.ts"
import { useOnePress } from "./use-one-press.ts"

interface Props {
  open: boolean
  onCancel(): void
  onDeclined(): void
  onDecline(reason: string): Promise<Outcome<unknown>>
}

export function DeclineModal({ open, onCancel, onDeclined, onDecline }: Props) {
  const { t } = useTranslation()
  const reasonId = useId()
  const problemId = useId()
  const [reason, setReason] = useState("")
  const [problem, setProblem] = useState<string | null>(null)
  const { busy: sending, run } = useOnePress()

  useEffect(() => {
    if (!open) return
    setReason("")
    setProblem(null)
  }, [open])

  const decline = () =>
    run(async (sending) => {
      if (!reason.trim()) return setProblem(t("signing.decline.required"))
      setProblem(null)
      sending()
      try {
        const outcome = await onDecline(reason)
        if (outcome.ok) onDeclined()
        else setProblem(refusalText(t, outcome))
      } catch {
        setProblem(t("signing.sign.failed"))
      }
    })

  return (
    <Modal
      open={open}
      title={t("signing.decline.title")}
      onCancel={onCancel}
      destroyOnHidden
      width={520}
      footer={
        <Flex justify="space-between" gap={8}>
          <Button onClick={onCancel} disabled={sending} style={TOUCH}>
            {t("signing.cancel")}
          </Button>
          <Button
            type="primary"
            danger
            loading={sending}
            disabled={sending}
            onClick={() => void decline()}
            style={TOUCH}
          >
            {t("signing.decline.confirm")}
          </Button>
        </Flex>
      }
    >
      <Flex vertical gap={12}>
        <Typography.Paragraph className="!mb-0">
          {t("signing.decline.body")}
        </Typography.Paragraph>
        <Flex vertical gap={4}>
          <label htmlFor={reasonId}>{t("signing.decline.reasonLabel")}</label>
          <TextArea
            id={reasonId}
            value={reason}
            maxLength={500}
            showCount
            autoSize={{ minRows: 4, maxRows: 8 }}
            status={problem ? "error" : undefined}
            aria-invalid={problem ? true : undefined}
            aria-describedby={problem ? problemId : undefined}
            onChange={(event) => setReason(event.target.value)}
          />
        </Flex>
        {problem && (
          <Alert
            type="error"
            showIcon
            message={<span id={problemId}>{problem}</span>}
          />
        )}
      </Flex>
    </Modal>
  )
}

export default DeclineModal
